// The emergencies nobody answered in time, one row each, newest first — the missed-calls list.
//
// Shown from the dashboard's corner pill and from the bell drawer. No Accept and no Decline: these
// are not ringing. What is left is what a phone offers for a missed call — call back, or clear it —
// and both are recorded on the server, so a missed emergency stays here until someone takes it.

import { useRobotsRoster } from '@/hooks/useRobotsRoster'
import type { RobotAlertItem } from '@/services/api/robot-alerts-store'
import { formatRelativeTime } from '@/utils/formatRelativeTime'
import { MapPin } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { indexRoster, resolveAlertLabel } from '../AlertInbox/alert-display'
import { useDashboardBridgeOptional } from '../Dashboard/useDashboardBridge'
import { deviceCanTakeCall, subscribeSosCallLive } from '../SosCall/sos-call-store'
import { singleClick } from './single-click'
import { useMissedEmergencyActions } from './useMissedEmergencyActions'

export interface MissedEmergencyListProps {
  items: RobotAlertItem[]
}

const BUTTON = 'rounded-md px-2.5 py-1 text-[11px] font-semibold'

export function MissedEmergencyList({ items }: MissedEmergencyListProps) {
  const { t } = useTranslation('console')
  const { robots } = useRobotsRoster(items.length > 0)
  const bridge = useDashboardBridgeOptional()
  const { notices, act } = useMissedEmergencyActions()
  const [callBusy, setCallBusy] = useState(false)

  useEffect(() => subscribeSosCallLive(setCallBusy), [])

  const roster = indexRoster(robots)

  return (
    <div data-testid="sos-missed-list">
      <ul className="divide-y divide-gray-100 dark:divide-white/10">
        {items.map(item => {
          const label = resolveAlertLabel(item, roster) ?? t('alerts.sos.unknownDevice', 'Unknown device')
          const device = robots.find(r => r.id === item.robotId)
          // UNKNOWN is not CANNOT — the same rule as Accept on a ringing card: an absent roster record
          // stays callable and the attempt decides.
          const callable = !callBusy && (device === undefined || deviceCanTakeCall(device))
          const notice = notices.get(item.alertId)
          const hasFix = typeof item.lng === 'number' && typeof item.lat === 'number'

          return (
            <li key={item.alertId} className="px-3 py-2" data-testid={`sos-missed-row-${item.alertId}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100" title={item.robotId}>
                    {label}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    <span className="font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                      {item.type ? item.type.replace(/_/g, ' ') : t('alerts.sos.title', 'Emergency')}
                    </span>
                    {' · '}
                    {t('alerts.sos.missedAgo', 'Missed · {{when}}', {
                      when: formatRelativeTime(item.lastSeenAt ?? item.recordedAt),
                    })}
                  </p>
                </div>
                {bridge && hasFix && (
                  <button
                    type="button"
                    onClick={() => bridge.emit('zoom-to-location', { center: [item.lng!, item.lat!], zoom: 15 })}
                    title={t('alerts.log.showOnMap', 'Show on map')}
                    aria-label={t('alerts.log.showOnMap', 'Show on map')}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-gray-200"
                  >
                    <MapPin className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </div>
              {/* On the row it is about: the list reorders as rows leave, and a reason at the top
                  would not say which worker it belongs to. */}
              {notice && (
                <p className="mt-1 text-[11px] text-red-700 dark:text-red-300" role="alert">
                  {notice}
                </p>
              )}
              <div className="mt-1.5 flex justify-end gap-2">
                <button
                  type="button"
                  data-testid="sos-missed-dismiss"
                  onClick={singleClick(() => act(item, 'dismiss'))}
                  className={`${BUTTON} border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700`}
                >
                  {t('alerts.sos.dismiss', 'Dismiss')}
                </button>
                {callable && (
                  <button
                    type="button"
                    data-testid="sos-missed-call-back"
                    onClick={singleClick(() => act(item, 'callBack', { label }))}
                    className={`${BUTTON} bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500`}
                  >
                    {t('alerts.sos.callBack', 'Call back')}
                  </button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
